import { NONE_OPTION_VALUE } from '../../constants';
import type {
  DiamLocationModifier,
  DiamModifierGroup,
  DiamModifierType,
} from '../../diamApi';
import { OptionalModifierFieldConfig, SelectOption, TreeNode } from './types';

const DEDICATED_MODIFIER_FIELDS = new Set<DiamModifierType>([
  'account',
  'location',
]);

const OPTIONAL_MODIFIER_LABELS: Partial<Record<DiamModifierType, string>> = {
  expires: 'Expires',
};

function isLocationModifier(
  item: string | DiamLocationModifier,
): item is DiamLocationModifier {
  return typeof item === 'object' && Boolean(item?.code);
}

function mapLocationNodes(
  locations: DiamLocationModifier[] | undefined,
): TreeNode[] {
  return (locations ?? []).map(location => {
    const children = mapLocationNodes(location.children);
    const count = children.reduce(
      (total, child) => total + (child.count ?? 0),
      0,
    );

    return {
      id: location.code,
      label: location.name,
      count: children.length > 0 ? count : 1,
      ...(children.length > 0 ? { children } : {}),
    };
  });
}

export function mapAccountOptions(
  modifiers: DiamModifierGroup[] | undefined,
): SelectOption[] {
  const accounts =
    modifiers
      ?.find(modifier => modifier.type === 'account')
      ?.items?.filter((item): item is string => typeof item === 'string') ?? [];

  return [
    { label: NONE_OPTION_VALUE, value: NONE_OPTION_VALUE },
    ...accounts.map(account => ({ label: account, value: account })),
  ];
}

export function mapLocationTree(
  modifiers: DiamModifierGroup[] | undefined,
): TreeNode[] {
  const locations =
    modifiers
      ?.find(modifier => modifier.type === 'location')
      ?.items?.filter(isLocationModifier) ?? [];

  return mapLocationNodes(locations);
}

export function mapOptionalModifierFields(
  modifiers: DiamModifierGroup[] | undefined,
): OptionalModifierFieldConfig[] {
  return (modifiers ?? [])
    .filter(modifier => !DEDICATED_MODIFIER_FIELDS.has(modifier.type))
    .map(modifier => {
      const options = (modifier.items ?? []).filter(
        (item): item is string => typeof item === 'string' && Boolean(item),
      );

      if (options.length > 0) {
        return {
          id: modifier.type,
          label: OPTIONAL_MODIFIER_LABELS[modifier.type] ?? modifier.type,
          type: 'dropdown' as const,
          options: [
            { label: NONE_OPTION_VALUE, value: NONE_OPTION_VALUE },
            ...options.map(option => ({ label: option, value: option })),
          ],
        };
      }

      return {
        id: modifier.type,
        label: OPTIONAL_MODIFIER_LABELS[modifier.type] ?? modifier.type,
        type: 'text' as const,
        placeholder:
          modifier.type === 'expires' ? '2026-10-29T12:00:00Z' : undefined,
        format:
          modifier.type === 'expires' ? ('iso-instant' as const) : undefined,
      };
    });
}
